import React, { forwardRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import FormField from '@/components/common/form-fields/FormField';
import { Controller, Control, FieldValues, Path, RegisterOptions } from 'react-hook-form';

export interface PasswordInputFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'name'> {
  label?: string;
  description?: string;
  error?: string;
  required?: boolean;
  // React Hook Form props (optional)
  name?: string;
  control?: Control<any>;
  rules?: RegisterOptions;
}

/**
 * Password input field with toggle visibility button
 * Supports both regular usage and react-hook-form integration
 */
const PasswordInputField = forwardRef<HTMLInputElement, PasswordInputFieldProps>(
  ({ label, description, error, required = false, className = '', name, control, rules, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);

    const inputElement = (field?: any) => (
      <div className="relative">
        <input
          {...props}
          {...(field || {})}
          ref={field?.ref || ref}
          type={showPassword ? 'text' : 'password'}
          className={`w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 text-xs sm:text-sm border rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${
            error ? 'border-red-500 focus:ring-red-500' : 'border-gray-200'
          } ${className}`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 transition-colors"
          title={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {showPassword ? (
            <EyeOff className="w-4 h-4 text-gray-500" />
          ) : (
            <Eye className="w-4 h-4 text-gray-500" />
          )}
        </button>
      </div>
    );

    // If using react-hook-form
    if (control && name) {
      return (
        <div className="w-full">
          {label && (
            <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          <Controller
            name={name as Path<FieldValues>}
            control={control}
            rules={rules}
            render={({ field }) => inputElement(field)}
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          {description && !error && <p className="text-gray-500 text-xs mt-1">{description}</p>}
        </div>
      );
    }

    // Regular usage without react-hook-form
    return (
      <FormField
        label={label}
        description={description}
        error={error}
        required={required}
      >
        {inputElement()}
      </FormField>
    );
  }
);

PasswordInputField.displayName = 'PasswordInputField';

export default PasswordInputField;
